import { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { markUncollectable } from 'api/campaigns';
import { Bid } from 'types/domain';

interface CancelBidAlertDialogProps {
    campaignId: number;
    auctionId: number;
    bid: Bid;
    onCancelBid?: () => void;
    children?: ReactNode;
}

const CancelBidAlertDialog = (props: CancelBidAlertDialogProps) => {
    const { auctionId, campaignId, onCancelBid, children, bid } = props;
    const toast = useToast();

    const { mutate: handleCancelBid } = useMutation({
        mutationFn: () => markUncollectable(campaignId, auctionId, bid.id),
        onSuccess: () => {
            toast.success('Successfully marked bid as disqualified');
            onCancelBid?.();
        },
        onError: () => {
            toast.error('Unable to mark bid disqualified');
        },
    });

    return (
        <AlertDialog.Root type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Cancel bid</AlertDialog.Title>
                        <AlertDialog.Description>
                            Cancelling a bid is an irreversible action.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            className={`disabled:cursor-not-allowed disabled:opacity-60`}
                            disabled={!bid.can_disqualify}
                            onClick={() => handleCancelBid()}
                        >
                            Cancel bid
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const CancelBidTrigger = AlertDialog.Trigger;

const Root = CancelBidAlertDialog;
const Trigger = CancelBidTrigger;

export {
    CancelBidAlertDialog,
    CancelBidTrigger,
    //
    Root,
    Trigger,
};
export type { CancelBidAlertDialogProps };
